/**
 * Kanzler-Platz im Zwei-Spieler-Modus.
 *
 * Die Engine kennt eine Partei „YOU“, die regiert. Im Mehrspieler sitzt dort, wer den
 * Regierungsauftrag bekommen hat: ein Spieler oder eine Computerpartei. Nach jeder Wahl wird
 * der Platz geräumt (`unslot`), die stärkste Partei bekommt den Auftrag (`slot`). Findet sie
 * keine Mehrheit, gibt sie ihn zurück und die nächste Partei ist dran.
 * Eine Computerpartei bildet ihre Koalition selbst (`aiForm`). Braucht sie einen Spieler,
 * bekommt der ein Angebot und entscheidet.
 */
import { NEED } from '../content/consts';
import { AX } from '../content/axes';
import { PARTIES, syncRegistry } from '../content/parties';
import { defaultRess } from './ministers';
import { startGovern } from './govern';
import { PN, lr, scoreRaw } from './rules';
import { S, log } from './state';
import { ROLES, pl, isVersus, playerByPid, nonChan, versusOnNextLeg, type Player } from './versus';

/** Partei-Kennung in allen Teilen des Zustands umbenennen, die Parteien referenzieren. */
function rename(from: string, to: string) {
  const R = S.res;
  if (R) {
    if (from in R.share) { R.share[to] = R.share[from]; delete R.share[from]; }
    if (from in R.seats) { R.seats[to] = R.seats[from]; delete R.seats[from]; }
    R.inP = R.inP.map((x) => (x === from ? to : x));
  }
  for (const k in S.gov || {}) if (S.gov[k] === from) S.gov[k] = to;
  S.partners = S.partners.map((x) => (x === from ? to : x));
  for (const r in S.ress || {}) if (S.ress[r] === from) S.ress[r] = to;
  for (const r in S.min || {}) if (S.min[r] && S.min[r].p === from) S.min[r].p = to;
  if (S.fin === from) S.fin = to;
  for (const L of S.laws) if (L.by === from) L.by = to;
}

/** Den Kanzler-Platz räumen: Die Partei bekommt wieder ihre eigene Kennung. */
export function unslot() {
  const M = S.mp; if (!M || !M.chan) return;
  if (M.chan === 'ai') { rename('YOU', M.ai!); M.ai = null; }
  else rename('YOU', pl(M.chan).pid);
  M.chan = null; S.name = '–'; S.abbr = null; S.color = null; S.youW = null;
  syncRegistry(M);
}

const fill = (pos: Record<string, number>) => Object.fromEntries(AX.map((a: string) => [a, pos[a] ?? 0]));

/** Partei auf den Kanzler-Platz setzen. */
export function slot(id: string) {
  const M = S.mp!; const before = M.chan === 'ai' ? M.ai : M.chan ? pl(M.chan).pid : null;
  unslot();
  const P = ROLES.map(pl).find((p) => p.pid === id && p.party);
  const X = P ? P.party! : PARTIES[id];
  if (P) M.chan = P.role; else { M.chan = 'ai'; M.ai = id; }
  S.name = X.n; S.abbr = X.a; S.color = X.c; S.you = fill(X.pos); S.youW = fill(X.w);
  rename(id, 'YOU'); syncRegistry(M);
  if (S.res) { S.res.inP.sort((a, b) => lr(a) - lr(b)); S.elec = S.res.share.YOU || 0; }
  if (before !== id) { S.wing = { soz: 60, wirt: 60 }; S.ptagLeg = -1; S.promises = []; S.memberFail = null; }
}

/** Nach der Wahl: Reihenfolge der Regierungsaufträge festlegen und den ersten vergeben. */
export function startFormation() {
  const M = S.mp!; unslot();
  if (S.res.share.YOU != null && !S.res.inP.includes('YOU')) delete S.res.share.YOU;
  M.mandate = S.res.inP.slice().sort((a, b) => S.res.share[b] - S.res.share[a]);
  M.mi = 0;
  for (const r of ROLES) { const P = pl(r); P.offer = null; P.contractOk = false; P.wish = null; }
  S.partners = []; S.agree = null; S.ress = {}; S.fin = 'YOU'; S.phase = 'night'; S.memberFail = null;
  giveMandate();
}

function giveMandate() {
  const M = S.mp!; slot(M.mandate[M.mi]);
  log(`${PN('YOU')} erhält als ${M.mi === 0 ? 'stärkste Partei' : 'nächste Partei'} den Auftrag zur Regierungsbildung.`);
  if (M.chan === 'ai') aiForm();
}

/** Der Kanzler-Kandidat gibt den Auftrag zurück, die nächste Partei ist dran. */
export function mandateReturn() {
  const M = S.mp; if (!M || S.phase !== 'night') return;
  log(`${PN('YOU')} findet keine Mehrheit und gibt den Auftrag zur Regierungsbildung zurück.`);
  S.partners = []; for (const P of nonChan()) P.offer = null;
  M.mi++;
  if (M.mi >= M.mandate.length) {
    // Niemand findet eine Mehrheit: Die stärkste Partei regiert in der Minderheit.
    M.mi = 0; slot(M.mandate[0]);
    log(`Keine Partei findet eine Mehrheit. ${PN('YOU')} bildet eine Minderheitsregierung.`, 'bad');
    if (M.chan === 'ai') aiGovern();
    return;
  }
  giveMandate();
}

/** Wie nah ist eine Partei dem Programm der Computerpartei auf dem Kanzler-Platz? */
const sim = (k: string) => scoreRaw(S.you, S.youW || {}, fill(PARTIES[k].pos));

/** Computerpartei sucht sich eine Koalition: wenige Partner, möglichst nah am eigenen Programm. */
export function aiForm() {
  const M = S.mp; if (!M || M.chan !== 'ai' || S.phase !== 'night') return;
  const you = S.res.seats.YOU || 0;
  if (you >= NEED) { S.partners = []; aiGovern(); return; }
  // Niemand koaliert mit Heimat & Ordnung, und Heimat & Ordnung findet keine Partner.
  const cands = S.res.inP.filter((k) => k !== 'YOU' && k !== 'HO' && M.ai !== 'HO')
    .filter((k) => { const P = playerByPid(k); return P ? P.offer !== 'declined' : sim(k) >= .55; });
  let best: string[] | null = null, bestKey = -1;
  const pick = (from: number, cur: string[]) => {
    if (cur.length) {
      const seats = you + cur.reduce((s, k) => s + S.res.seats[k], 0);
      if (seats >= NEED) {
        const key = (4 - cur.length) * 10 + cur.reduce((s, k) => s + sim(k), 0) / cur.length;
        if (key > bestKey) { bestKey = key; best = cur.slice(); }
        return;
      }
    }
    if (cur.length >= 3) return;
    for (let i = from; i < cands.length; i++) pick(i + 1, [...cur, cands[i]]);
  };
  pick(0, []);
  if (!best) { mandateReturn(); return; }
  S.partners = best;
  const wait: Player[] = (best as string[]).map(playerByPid).filter((p): p is Player => !!p && p.offer !== 'accepted');
  for (const P of wait) if (P.offer !== 'pending') { P.offer = 'pending'; log(`${PN('YOU')} bietet ${PN(P.pid)} eine Koalition an.`); }
  if (!wait.length) aiGovern();
}

/** Koalitionsvertrag der Computerpartei (sitzgewichteter Mittelweg) und Regierungsantritt. */
export function aiGovern() {
  const ids = ['YOU', ...S.partners], seats = (id: string) => S.res.seats[id] || 0, tot = ids.reduce((s, id) => s + seats(id), 0) || 1;
  S.agree = Object.fromEntries(AX.map((a: string) => [a, Math.round(ids.reduce((s, id) => s + (id === 'YOU' ? S.you[a] : PARTIES[id].pos[a] ?? 0) * seats(id), 0) / tot)]));
  S.ress = defaultRess();
  log(S.partners.length ? `${PN('YOU')} bildet eine Koalition mit ${S.partners.map(PN).join(' und ')}.` : `${PN('YOU')} regiert allein.`, '');
  startGovern();
}

/** Nach einer Antwort eines Spielers auf ein Angebot der Computerpartei weiter verhandeln. */
export function aiAfterReply() { if (isVersus() && S.mp!.chan === 'ai' && S.phase === 'night') aiForm(); }

/** Neue Legislatur nach einer Wahl im Mehrspieler. */
export function nextLegislature(first: boolean) {
  if (!first) { S.leg++; S.m += 2; S.early = false; S.end = null; versusOnNextLeg(); }
  S.mp!.first = false;
  startFormation();
}

